import React, { useState } from 'react';
import { IconButton, Menu, MenuItem, ListItemIcon, ListItemText } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';

import PropTypes from 'prop-types';

/**
 * KebabCommandCell
 *
 * A custom cellComponent for TableEditColumn that collapses the Edit and Delete
 * command buttons into a kebab (⋮) dropdown menu.
 *
 * The grid passes already-wired command buttons as `children`. Each child has
 * props: { id: 'edit' | 'delete' | ..., onExecute: () => void }.
 * We extract those onExecute callbacks and hook them into the menu items.
 *
 * The "Add" command is rendered in the *header* cell (not data cells), so it
 * is completely unaffected by this component and works as normal.
 */
const KebabCommandCell = ({ children, onPopupEditStart, ...rest }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const handleClose = (event) => {
    event?.stopPropagation?.();
    setAnchorEl(null);
  };

  // Extract onExecute callbacks from the grid-provided command children.
  // Each child is a rendered command component with props: { id, onExecute }
  const commandMap = {};
  React.Children.forEach(children, (child) => {
    if (child && child.props && child.props.id) {
      commandMap[child.props.id] = child.props.onExecute;
    }
  });

  const handleEdit = (event) => {
    event.stopPropagation();
    if (commandMap['edit']) {
      onPopupEditStart();
      commandMap['edit']();
    }
    handleClose();
  };

  const handleDelete = (event) => {
    event.stopPropagation();
    if (commandMap['delete']) {
      commandMap['delete']();
    }
    handleClose();
  };

  // If there are no edit/delete commands (e.g., this is the header "add" row),
  // render nothing — the header cell handles its own rendering.
  const hasActions = commandMap['edit'] || commandMap['delete'];
  if (!hasActions) {
    return <td {...rest} />;
  }

  return (
    <td
      onClick={(event) => event.stopPropagation()}
      onDoubleClick={(event) => event.stopPropagation()}
      style={{
        borderBottom: '1px solid #e0e0e0',
        padding: '16px',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <IconButton aria-label="row actions" onClick={handleClick} size="small">
        <MoreVertIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        onClick={(event) => event.stopPropagation()}
        onMouseDown={(event) => event.stopPropagation()}
      >
        {commandMap['edit'] && (
          <MenuItem onClick={handleEdit}>
            <ListItemIcon>
              <EditIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Edit</ListItemText>
          </MenuItem>
        )}
        {commandMap['delete'] && (
          <MenuItem onClick={handleDelete}>
            <ListItemIcon>
              <DeleteIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>Delete</ListItemText>
          </MenuItem>
        )}
      </Menu>
    </td>
  );
};

export default KebabCommandCell;

KebabCommandCell.propTypes = {
  children: PropTypes.any,
  onPopupEditStart: PropTypes.func
};

KebabCommandCell.defaultProps = { onPopupEditStart: () => {} };
